/**
 * EventPopup — what an `E` or `N` marker says when you hover it.
 *
 * `position: fixed`, for `Menu`'s reason and then one more: the chart's
 * container clips (`overflow: hidden` on the panel, and a sub-surface has no DOM
 * node to hang off at all), so an absolutely-positioned panel would be cut off
 * by the pane it belongs to. Fixed takes it out of that context at the cost of
 * having to be placed against a client point, which is what the builder reports.
 *
 * **Inert to the pointer.** `pointerEvents: "none"` throughout: this appears
 * because the pointer is over a glyph on the canvas, and a panel that could take
 * the pointer would steal it from the very thing keeping the panel open — the
 * hover would end, the panel would close, and it would flicker at its own edge.
 * Nothing in it is clickable, so nothing is lost.
 */

import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import type { EventMarker } from "@qufolio/chart-engine";
import type { EarningsQuarter } from "../api/symbols";

export interface EventPopupProps {
  marker: EventMarker;
  /** Client coordinates of the glyph. */
  at: { x: number; y: number };
  /** The quarter behind an earnings marker; absent for news. */
  quarter?: EarningsQuarter;
  /** Reported EPS and revenue, YoY and QoQ, for that quarter. */
  growth?: {
    eps?: { yoy: number | null; qoq: number | null };
    revenue?: { yoy: number | null; qoq: number | null };
  };
  /** Headlines behind a news marker. */
  headlines?: readonly string[];
  currency?: string;
  /**
   * The next report, not yet made (owner, 2026-10-28): the consensus and its
   * change against the reported quarters, with no reported or surprise rows.
   */
  upcoming?: boolean;
}

/** Roughly the panel's size, for keeping it on screen. */
const WIDTH = 232;
const ESTIMATED_HEIGHT = 260;
const GAP = 12;
/** Clear of the viewport edge, so a clamped panel does not look severed. */
const EDGE = 8;

export function EventPopup({ marker, at, quarter, growth, headlines, currency, upcoming }: EventPopupProps) {
  // **The height is measured, not estimated — and the estimate is why this was
  // a bug.** A news panel is as tall as its headlines and an earnings one as
  // tall as its rows, so 260 was only ever right by luck. It decided the flip
  // *and* the top edge, so with the price pane dragged low the glyph sat near
  // the bottom of the window, the panel flipped below it, and the last rows
  // were cut off by the window rather than by any container — nothing here
  // scrolls, because the whole panel is `pointerEvents: none`.
  //
  // `useLayoutEffect` and not `useEffect`: it runs before the browser paints,
  // so the corrected placement is the first thing drawn rather than a frame of
  // the panel jumping. The estimate survives as the first-render guess, which
  // is all it was ever fit for. No dependency array — the content changes with
  // the marker, and the width is fixed, so re-measuring cannot feed back into
  // the height and loop.
  const panel = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(ESTIMATED_HEIGHT);
  useLayoutEffect(() => {
    const measured = panel.current?.offsetHeight;
    if (measured && Math.abs(measured - height) > 1) setHeight(measured);
  });

  // Above the glyph, which sits at the foot of the price pane — so the panel
  // opens over the chart rather than over the date axis under it. Flipped below
  // only when there is genuinely no room above, and then to whichever side has
  // more room when it fits neither: a pane near the bottom of a tall window has
  // the space above it and nowhere else.
  const roomAbove = at.y - GAP - EDGE;
  const roomBelow = window.innerHeight - at.y - GAP - EDGE;
  const above = height <= roomAbove || roomAbove >= roomBelow;

  // Clamped on both axes, and the vertical clamp is the fix: a marker can sit
  // hard against any edge of a chart that is itself a pane of a split layout,
  // and the panel belongs to the window, not to the pane.
  const top = Math.max(
    EDGE,
    Math.min(
      above ? at.y - GAP - height : at.y + GAP,
      window.innerHeight - height - EDGE,
    ),
  );
  const left = Math.max(
    EDGE,
    Math.min(at.x - WIDTH / 2, window.innerWidth - WIDTH - EDGE),
  );

  return (
    <div
      ref={panel}
      style={{
        ...PANEL,
        top,
        left,
        width: WIDTH,
        // A panel taller than the window cannot be placed anywhere that fits,
        // so it is capped: the measurement then reports the capped height and
        // the clamp above lands exactly on the top edge. Only a long news list
        // reaches this, and it was being cut by the window's edge before —
        // this cuts it at a border instead.
        maxHeight: window.innerHeight - 2 * EDGE,
      }}
    >
      <div style={HEAD}>
        <span style={{ ...BADGE, borderColor: toneColor(marker.tone), color: toneColor(marker.tone) }}>
          {marker.label}
        </span>
        <span style={TITLE}>{marker.title ?? "Event"}</span>
      </div>

      {quarter && upcoming ? <Upcoming quarter={quarter} growth={growth} currency={currency} /> : null}
      {quarter && !upcoming ? <Earnings quarter={quarter} growth={growth} currency={currency} /> : null}
      {headlines ? <Headlines headlines={headlines} /> : null}
    </div>
  );
}

function Earnings({
  quarter,
  growth,
  currency,
}: {
  quarter: EarningsQuarter;
  growth?: EventPopupProps["growth"];
  currency?: string;
}) {
  return (
    <>
      <Row label="Date" value={longDate(quarter.date)} />
      <Row label="Period Ending" value={monthYear(quarter.periodEnd)} />

      <Section>Earnings</Section>
      <Row label="Reported" value={num(quarter.epsActual)} />
      <Row label="Estimate" value={num(quarter.epsEstimated)} />
      <Surprise value={quarter.epsSurprise} fraction={quarter.epsSurprisePct} />
      <Growth label="EPS YoY" fraction={growth?.eps?.yoy} />
      <Growth label="EPS QoQ" fraction={growth?.eps?.qoq} />

      <Section>Revenue</Section>
      <Row label="Reported" value={money(quarter.revenue, currency)} />
      <Row label="Estimate" value={money(quarter.revenueEstimated, currency)} />
      <Surprise
        value={quarter.revenueSurprise}
        fraction={quarter.revenueSurprisePct}
        format={(v) => money(v, currency)}
      />
      <Growth label="Rev YoY" fraction={growth?.revenue?.yoy} />
      <Growth label="Rev QoQ" fraction={growth?.revenue?.qoq} />
    </>
  );
}

/** The next report: what has to be cleared, and what clearing it would mean. */
function Upcoming({
  quarter,
  growth,
  currency,
}: {
  quarter: EarningsQuarter;
  growth?: EventPopupProps["growth"];
  currency?: string;
}) {
  return (
    <>
      <Row label="Date" value={longDate(quarter.date)} />
      <Row label="Period Ending" value={monthYear(quarter.periodEnd)} />

      <Section>Earnings</Section>
      <Row label="Estimate" value={num(quarter.epsEstimated)} />
      <Growth label="Est EPS YoY" fraction={growth?.eps?.yoy} />
      <Growth label="Est EPS QoQ" fraction={growth?.eps?.qoq} />

      <Section>Revenue</Section>
      <Row label="Estimate" value={money(quarter.revenueEstimated, currency)} />
      <Growth label="Est Rev YoY" fraction={growth?.revenue?.yoy} />
      <Growth label="Est Rev QoQ" fraction={growth?.revenue?.qoq} />
    </>
  );
}

function Headlines({ headlines }: { headlines: readonly string[] }) {
  if (headlines.length === 0) {
    return <div style={{ ...VALUE, padding: "4px 0" }}>Nothing recent.</div>;
  }
  return (
    <>
      {headlines.map((headline, i) => (
        <div key={i} style={HEADLINE}>
          {headline}
        </div>
      ))}
    </>
  );
}

/**
 * The surprise line, and the one row that carries a colour.
 *
 * **Absent rather than zero when there was no estimate.** A quarter nobody
 * forecast has no surprise, and printing `0.00 (0.00%)` for it would claim the
 * company landed exactly on a number that was never struck.
 */
function Surprise({
  value,
  fraction,
  format = num,
}: {
  value?: number | null;
  fraction?: number | null;
  format?: (v?: number | null) => string;
}) {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  const tone = value > 0 ? "positive" : value < 0 ? "negative" : "neutral";
  const pct =
    fraction === null || fraction === undefined || !Number.isFinite(fraction)
      ? null
      : `${(Math.abs(fraction) * 100).toFixed(2)}%`;
  return (
    <div style={ROW}>
      <span style={{ ...LABEL, color: toneColor(tone) }}>Surprise</span>
      <span style={{ ...VALUE, color: toneColor(tone) }}>
        {format(value)}
        {pct ? ` (${pct})` : ""}
      </span>
    </div>
  );
}

/**
 * A reported-EPS change, signed and coloured by sign. Absent when there is no
 * report to compare with — a row reading "—" for the first quarter on record
 * says nothing the missing row does not.
 */
function Growth({ label, fraction }: { label: string; fraction?: number | null }) {
  if (fraction === null || fraction === undefined || !Number.isFinite(fraction)) return null;
  const printed = (fraction * 100).toFixed(1);
  const tone = Number(printed) > 0 ? "positive" : Number(printed) < 0 ? "negative" : "neutral";
  return (
    <div style={ROW}>
      <span style={LABEL}>{label}</span>
      <span style={{ ...VALUE, color: toneColor(tone) }}>
        {Number(printed) > 0 ? "+" : ""}
        {printed}%
      </span>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={ROW}>
      <span style={LABEL}>{label}</span>
      <span style={VALUE}>{value}</span>
    </div>
  );
}

function Section({ children }: { children: string }) {
  return <div style={SECTION}>{children}</div>;
}

/* ── formatting ──────────────────────────────────────────────────────────── */

const toneColor = (tone: EventMarker["tone"]): string =>
  tone === "positive"
    ? "var(--qf-positive)"
    : tone === "negative"
      ? "var(--qf-negative)"
      : tone === "pending"
        ? "var(--qf-warn)"
        : "var(--qf-text-secondary)";

/** `Thu 16 Apr '26` — a weekday, because "was that a Monday?" is the question. */
function longDate(date?: string | null): string {
  if (!date) return "—";
  const when = new Date(`${date.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(when.getTime())) return "—";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "2-digit",
  }).format(when);
  // `Thu, 16 Apr 26` -> `Thu 16 Apr '26`, which is the reference's own spelling.
  return parts.replace(",", "").replace(/(\d\d)$/, "'$1");
}

/** `Mar '26` — a quarter is named by the month it ended, never by a day. */
function monthYear(date?: string | null): string {
  if (!date) return "—";
  const when = new Date(`${date.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(when.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    month: "short",
    year: "2-digit",
  })
    .format(when)
    .replace(/(\d\d)$/, "'$1");
}

/**
 * Three decimals, trimmed.
 *
 * An estimate is quoted to three (`1.965`) and a result to two (`2.24`), and
 * rounding the estimate to match would make a beat of `0.275` look like `0.28`
 * against a number that never existed.
 */
function num(value?: number | null): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  const sign = value < 0 ? "−" : "";
  const text = Math.abs(value).toFixed(3).replace(/\.?0+$/, "");
  return `${sign}${text || "0"}`;
}

/** `5.41 B` — revenue is read as a magnitude, never as fourteen digits. */
function money(value?: number | null, currency?: string): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  const sign = value < 0 ? "−" : "";
  const size = Math.abs(value);
  const unit =
    size >= 1e12
      ? [1e12, "T"]
      : size >= 1e9
        ? [1e9, "B"]
        : size >= 1e6
          ? [1e6, "M"]
          : size >= 1e3
            ? [1e3, "K"]
            : [1, ""];
  const scaled = size / (unit[0] as number);
  const text = scaled.toFixed(scaled >= 100 ? 0 : 2).replace(/\.?0+$/, "");
  return `${sign}${symbolFor(currency)}${text}${unit[1] ? ` ${unit[1]}` : ""}`;
}

function symbolFor(currency?: string): string {
  if (!currency) return "";
  if (currency === "USD") return "$";
  if (currency === "INR") return "₹";
  if (currency === "EUR") return "€";
  if (currency === "GBP") return "£";
  return "";
}

/* ── styles ──────────────────────────────────────────────────────────────── */

const PANEL: CSSProperties = {
  position: "fixed",
  zIndex: 1000,
  // See the header: the panel exists because the pointer is elsewhere.
  pointerEvents: "none",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
  // Pairs with the `maxHeight` above: nothing here scrolls, so the cap has to
  // clip rather than spill past a border the eye reads as the panel's edge.
  overflow: "hidden",
  padding: "8px 10px 10px",
  font: "inherit",
  fontSize: 11,
  color: "var(--qf-text)",
};

const HEAD: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  marginBottom: 8,
};

const BADGE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 16,
  height: 16,
  borderRadius: 4,
  border: "1px solid",
  fontSize: 9,
  fontWeight: 700,
  flex: "0 0 auto",
};

const TITLE: CSSProperties = { fontWeight: 600, fontSize: 12 };

const SECTION: CSSProperties = {
  marginTop: 8,
  marginBottom: 2,
  fontSize: 9,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--qf-text-muted)",
};

const ROW: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: 12,
  padding: "1px 0",
};

const LABEL: CSSProperties = { color: "var(--qf-text-secondary)" };

const VALUE: CSSProperties = {
  // Tabular, so a column of numbers lines up on the decimal rather than
  // wandering with the glyph widths.
  fontVariantNumeric: "tabular-nums",
};

const HEADLINE: CSSProperties = {
  padding: "3px 0",
  borderTop: "1px solid var(--qf-border)",
  color: "var(--qf-text-secondary)",
  lineHeight: 1.35,
};
